import React, { useRef, useState } from "react";
import {
  Box,
  TableRow,
  TableCell,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";

const tdLabelSx = {
  width: 380,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 700,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1.5, verticalAlign: "middle" };
const noteSx = { color: "#666", fontSize: 12, lineHeight: 1.25 };

export default function ElectronicSignatureSettings({
  firmaEstado,
  firmadoAutomatico,
  onToggleAuto,
  onRefresh,
  getCSRF,
  setSuccessMessage,
  setErrorMessage,
}) {
  const fileRef = useRef(null);
  const [clave, setClave] = useState("");
  const [editando, setEditando] = useState(false);

  const firmaSubida = !!firmaEstado?.subida;
  const firmaValida = !!firmaEstado?.valida;

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const ext = f.name.split(".").pop().toLowerCase();
    if (ext !== "p12") {
      setErrorMessage("Solo se permite archivo .p12");
      e.target.value = "";
      return;
    }

    try {
      const fd = new FormData();
      fd.append("archivo", f);

      const r = await fetch("/facturas/firma/cargar/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRF() },
        body: fd,
      });

      if (r.ok) {
        setSuccessMessage("Firma subida");
        setEditando(true);
        onRefresh();
      } else {
        let j = {};
        try { j = await r.json(); } catch {}
        setErrorMessage(j.error || "No se pudo subir la firma");
      }
    } catch {
      setErrorMessage("Error de conexión");
    } finally {
      e.target.value = "";
    }
  };

  const guardarClave = async () => {
    if (!firmaSubida) {
      setErrorMessage("Primero sube la firma .p12");
      return;
    }
    if (!clave) {
      setErrorMessage("Ingresa la clave");
      return;
    }

    try {
      const fd = new FormData();
      fd.append("clave", clave);

      const r = await fetch("/facturas/clave/guardar/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRF() },
        body: fd,
      });

      if (r.ok) {
        setSuccessMessage("Firma validada correctamente");
        setClave("");
        setEditando(false);
        onRefresh();
      } else {
        let j = {};
        try { j = await r.json(); } catch {}
        setErrorMessage(j.error || "Clave incorrecta");
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  const removerFirma = async () => {
    try {
      const r = await fetch("/facturas/firma/remover/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRF() },
      });

      if (r.ok) {
        setSuccessMessage("Firma electrónica removida");
        setClave("");
        setEditando(false);
        onRefresh();
      } else {
        let j = {};
        try { j = await r.json(); } catch {}
        setErrorMessage(j.error || "No se pudo remover la firma");
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  const toggleAuto = async (checked) => {
    onToggleAuto(checked);
    try {
      const fd = new FormData();
      fd.append("auto", checked ? "1" : "0");

      const r = await fetch("/facturas/clave/guardar/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRF() },
        body: fd,
      });

      if (!r.ok) {
        onToggleAuto(!checked);
        let j = {};
        try { j = await r.json(); } catch {}
        setErrorMessage(j.error || "No se pudo guardar la opción");
      }
    } catch {
      onToggleAuto(!checked);
      setErrorMessage("Error de conexión");
    }
  };

  return (
    <>
      <TableRow>
        <TableCell sx={tdLabelSx}>
          Firma Electrónica
          <Box sx={noteSx}>(Firma Electrónica del Representante Legal)</Box>
        </TableCell>

        <TableCell sx={tdFieldSx}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            {(!firmaValida || editando) && (
              <Button
                variant="contained"
                startIcon={<FolderOpenIcon />}
                onClick={onPickFile}
                sx={{ textTransform: "none" }}
              >
                Seleccionar
              </Button>
            )}

            <input ref={fileRef} type="file" accept=".p12" hidden onChange={onFileChange} />

            {firmaSubida && !firmaValida && (
              <Button variant="outlined" disabled>
                Firma subida
              </Button>
            )}

            {firmaValida && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditando(true)}
                >
                  Modificar firma
                </Button>
                <Button
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={removerFirma}
                >
                  Remover firma
                </Button>
              </>
            )}
          </Box>

          {firmaValida && firmaEstado?.valido_hasta && (
            <Box sx={{ mt: 1, color: "#333", fontWeight: 400 }}>
              Firma válida hasta: {firmaEstado.valido_hasta}
            </Box>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={tdLabelSx}>Clave de la Firma</TableCell>
        <TableCell sx={tdFieldSx}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              type="password"
              size="small"
              label="Clave"
              disabled={firmaValida && !editando}
              value={clave}
              onChange={(e) => setClave(e.target.value)}
            />
            {(!firmaValida || editando) && (
              <Button variant="contained" onClick={guardarClave}>
                Validar
              </Button>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={tdLabelSx}>Firma Automática</TableCell>
        <TableCell sx={tdFieldSx}>
          <FormControlLabel
            control={
              <Checkbox
                checked={firmadoAutomatico}
                onChange={(e) => toggleAuto(e.target.checked)}
                disabled={!firmaValida}
              />
            }
          />
        </TableCell>
      </TableRow>
    </>
  );
}
